import { getReplay } from '@sentry/react';
import { cloneDeep, get, isEmpty, keyBy, mapValues, pipe, set } from 'lodash/fp';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  generatePath,
  matchPath,
  Navigate,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom-v5-compat';
import { formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import styled, { css } from 'styled-components';

import BottomPanel from 'dashboard/apps/InquiryTemplateEditor/BottomPanel';
import { MINIMUM_BOTTOM_PANEL_HEIGHT } from 'dashboard/apps/InquiryTemplateEditor/BottomPanel/BottomPanel';
import {
  CONFIG_EDITOR_RELATIVE_PATH,
  FLOW_EDITOR_BASE_PATH,
  FLOW_EDITOR_NODE_TOKEN_RELATIVE_PATH,
  FLOW_EDITOR_RELATIVE_PATH,
  FLOW_EDITOR_ROUTE_INDEX_RELATIVE_PATH,
  INQUIRY_TEMPLATE_EDITOR_DRAFT_PLACEHOLDER_ID,
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
  REVIEW_CHANGES_PATH,
  REVIEW_CHANGES_RELATIVE_PATH,
  SCREEN_EDITOR_BASE_PATH,
  SCREEN_EDITOR_COMBINED_RELATIVE_PATH,
  SCREEN_EDITOR_PATH,
  SCREEN_EDITOR_RELATIVE_PATH,
  SCREEN_EDITOR_STEP_PATH,
  SCREEN_EDITOR_STEP_RELATIVE_PATH,
  WORKFLOW_EDITOR_BASE_PATH,
  WORKFLOW_EDITOR_CONDITIONAL_ROUTE_RELATIVE_PATH,
  WORKFLOW_EDITOR_RELATIVE_PATH,
  WORKFLOW_EDITOR_STEP_TOKEN_PATH,
  WORKFLOW_EDITOR_STEP_TOKEN_RELATIVE_PATH,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { generatePages } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import type {
  InquiryTemplateEditorFormData,
  MatchRequirementProfileMap,
  StepPreviewMap,
  VerificationTemplateVersionMap,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  LinkedWorkflowStep,
  PreviewPageMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  extractActionsAndConditionalNodes,
  getFirstWorkflowValidationErrorNode,
  updateWorkflowSchemaInquiryFields,
  validationErrorsForWorkflowNode,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import InquiryTemplateFlowEditor from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor';
import { FlowTreeFormValues } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import {
  ComponentValidationError,
  ComponentValidationErrorsMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations/types';
import { SelectedComponentAndStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { mapStepsConfigToStepsPreview } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import InquiryTemplateVersionDiff from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiff';
import { VersionDiff } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import ShortcutsModal from 'dashboard/apps/InquiryTemplateEditor/ShortcutsModal';
import {
  ITE_BASE_SHORTCUTS,
  shortcutsMap,
} from 'dashboard/apps/InquiryTemplateEditor/ShortcutsModal/shortcuts';
import { firstInitialStep } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import LoadingModal from 'dashboard/components/LoadingModal';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import WorkflowActionMetaProvider from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider';
import { BANNER_MIN_HEIGHT } from 'dashboard/components-lib/Banner/Banner';
import type { EditorToolbarProps } from 'dashboard/components-lib/EditorToolbar';
import { ToolbarControlItem } from 'dashboard/components-lib/EditorToolbar';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop, FULL_SCREEN_HEADER_HEIGHT_XS } from 'dashboard/layouts/ShowViewLayoutV2';
import { InquiryTemplateVersionError } from 'dashboard/lib/interfaces/InquiryTemplateVersionErrors';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TransformationMatrix2D } from 'dashboard/lib/utils/cssTransformationMatrix';
import { validateWorkflowConfiguration } from 'dashboard/lib/workflows/validation';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateModule,
  InquiryTemplateModuleOption,
  ModuleOptionKey,
  ModulePreviewMap,
} from 'dashboard/models/inquiry-template-module';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStatus,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  PartialInquiryTemplateVersion,
} from 'dashboard/models/inquiry-template-version';
import { ThemeAttributes } from 'dashboard/models/theme';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { WorkflowActionMeta, WorkflowConfigError, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowStepInput } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useListExternalIntegrationVendorsQuery } from 'dashboard/redux/api/external-integration-vendor';
import { getAllWorkflowValidationContexts } from 'dashboard/redux/selectors/fle-workflow-context';
import { getWorkflowActionMeta } from 'dashboard/redux/selectors/workflow-action-meta';
import { InquiryTemplateSchema, setStepsPreview } from 'dashboard/redux/slices/inquiry-template';
import { RootState } from 'dashboard/redux/store';
import {
  generateAllWorkflowStepContexts,
  updateHistory,
} from 'dashboard/redux/thunk-actions/inquiry-template';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import useDebounced from 'lib/hooks/useDebounced';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import { captureMessage } from 'lib/reporting';

// InquiryTemplateFlowEditor is the heaviest editor component and the default "start" page so we do not lazy load it.
// For the others, we lazy load but prefetch them to improve the experience when navigating to them.
const InquiryTemplateScreenEditor = lazy(
  () => import('dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor'),
);
const InquiryTemplateConfigEditor = lazy(
  () => import('dashboard/apps/InquiryTemplateEditor/InquiryTemplateConfigEditor'),
);
const InquiryTemplateWorkflowEditor = lazy(
  () => import('dashboard/apps/InquiryTemplateEditor/InquiryTemplateWorkflowEditor'),
);

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;
const selector = formValueSelector(form);

const WORKFLOW_VALIDATION_ERROR_MESSAGE = 'Action sequence or branch node is invalid.';

const shouldDisplayBanner = (
  inquiryTemplate: InquiryTemplate,
  inquiryTemplateVersion: InquiryTemplateVersion,
): boolean => {
  const {
    relationships: {
      'latest-draft-version': latestDraftVersion,
      'latest-published-version': latestPublishedVersion,
    },
  } = inquiryTemplate;
  const isPublished =
    inquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Published;
  const hasNewerPublishedVersion = inquiryTemplateVersion.id !== latestPublishedVersion.data?.id;
  const hasDraft = latestDraftVersion.data?.id != null;
  return isPublished && (hasNewerPublishedVersion || hasDraft);
};

export type ITESecondaryEditorNav = Pick<
  EditorToolbarProps['controls'],
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ToolbarControlItem.Undo | ToolbarControlItem.Redo
>;

const InquiryTemplateEditorContainer = styled.div<{
  withBanner: boolean;
  showingBottomPanel: boolean;
}>`
  display: flex;
  /* Prevent absolutely positioned elements in preview from breaking out of container by making a separate stacking context */
  z-index: 0;
  ${({ withBanner }) => {
    return withBanner
      ? css`
          height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT_XS + BANNER_MIN_HEIGHT}px);
        `
      : css`
          height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT_XS}px);
        `;
  }}
  padding-bottom: ${({ showingBottomPanel }) =>
    showingBottomPanel ? `${MINIMUM_BOTTOM_PANEL_HEIGHT}px` : '0px'};
`;

const BottomPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  width: 100%;
  left: 0px;
  bottom: 0px;
  background: white;
`;

interface InquiryTemplateEditorProps {
  isLoading: boolean;
  inquiryTemplate: InquiryTemplate;
  inquiryTemplateVersion: InquiryTemplateVersion;
  partialInquiryTemplateVersions: PartialInquiryTemplateVersion[];
  locale: InquiryIso6391LanguageCode;
  basePath: string;
  submissionWorkflowErrors: Record<string, WorkflowConfigError[]> | null;
  matchRequirementProfileMap: MatchRequirementProfileMap;
  moduleOptions: InquiryTemplateModuleOption[];
  modulePreviewMap: ModulePreviewMap;
  isShowingInquiryTemplateVersions: boolean;
  isFormPristine: boolean;
  keydownShortcutsEnabled: boolean;
  submissionComponentErrors: ComponentValidationErrorsMap | undefined;
  workflowSchema: WorkflowSchema;
  versionDiff: VersionDiff | undefined;
  loadingVersionDiff: boolean;
  loadingComparedVersion: boolean;
  currentSchema: InquiryTemplateSchema;
  comparedSchema: InquiryTemplateSchema;
  comparedVersion: InquiryTemplateVersion | undefined;
  inputStep: WorkflowStepInput;
  refreshWorkflowSchema: boolean;
  validationErrors: InquiryTemplateVersionError[];
  allowSavingInvalidItvDraft: boolean;
  handleAddLocalDraftVersion: () => void;
  handleDiscardDraft: () => Promise<void>;
  handleLoadModule: (
    moduleKey: ModuleOptionKey,
    currentFieldSchema: FieldSchema[],
  ) => Promise<InquiryTemplateModule>;
  handleLoadModulePreview: (moduleKey: ModuleOptionKey) => Promise<void>;
  handleLoadVersionDiff: () => void;
  handleLoadComparedVersion: () => void;
  handleLoadStepLocalizedStrings: (
    stepNames?: Array<string>,
    locales?: Array<InquiryIso6391LanguageCode>,
  ) => Promise<void>;
  handleSelectVersion: (inquiryTemplateVersion: string) => void;
  handleSetLocale: (locale: InquiryIso6391LanguageCode) => void;
  handleUpdateStoredVerificationTemplates: (verificationTemplates: VerificationTemplate[]) => void;
  updateValidationErrors: (formData: InquiryTemplateEditorFormData) => void;
  hideInquiryTemplateVersions: () => void;
  setRefreshWorkflowSchema: (val: boolean) => void;
}

const InquiryTemplateEditor = ({
  isLoading,
  inquiryTemplate,
  inquiryTemplateVersion,
  partialInquiryTemplateVersions,
  locale,
  basePath,
  submissionWorkflowErrors,
  matchRequirementProfileMap,
  moduleOptions,
  modulePreviewMap,
  isShowingInquiryTemplateVersions,
  isFormPristine,
  keydownShortcutsEnabled,
  submissionComponentErrors,
  workflowSchema,
  versionDiff,
  loadingVersionDiff,
  loadingComparedVersion,
  currentSchema,
  comparedSchema,
  comparedVersion,
  inputStep,
  refreshWorkflowSchema,
  validationErrors,
  change,
  handleAddLocalDraftVersion,
  handleDiscardDraft,
  handleLoadModule,
  handleLoadModulePreview,
  handleLoadStepLocalizedStrings,
  handleLoadVersionDiff,
  handleLoadComparedVersion,
  handleSelectVersion,
  handleSetLocale,
  handleUpdateStoredVerificationTemplates,
  hideInquiryTemplateVersions,
  setRefreshWorkflowSchema,
}: InquiryTemplateEditorProps & InjectedFormProps) => {
  useEffect(() => {
    try {
      getReplay()?.startBuffering();
    } catch {
      // Do nothing if this errors
    }
  }, []);

  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allowSavingInvalidItvDraft = useFeatureFlag('inquiry:enable-saving-invalid-itv-drafts');
  const enableLeftNav = useFeatureFlag('dashboard:fle-redesign-left-nav-and-panels');

  // save our spot in pan zoom view for when we return to tree editor
  const savedFlowViewTransformationMatrixRef = useRef<TransformationMatrix2D | undefined>(
    undefined,
  );
  const setSavedFlowViewTransformationMatrix = (matrix: TransformationMatrix2D | undefined) => {
    savedFlowViewTransformationMatrixRef.current = matrix;
  };
  const [debouncedSetSavedFlowViewTransformationMatrix] = useDebounced(
    setSavedFlowViewTransformationMatrix,
    500,
  );

  const [shouldShowShortcutsModal, { enable: showShortcutsModal, disable: hideShortcutsModal }] =
    useToggle(false);
  const [selectedComponentAndStep, setRawSelectedComponentAndStep] =
    useState<SelectedComponentAndStep>(null);

  // The version id to use in the path; is "draft" placeholder value when the showing
  // a draft not yet initially
  const pathInquiryTemplateVersionId =
    inquiryTemplateVersion.id ?? INQUIRY_TEMPLATE_EDITOR_DRAFT_PLACEHOLDER_ID;

  // use object wrapped around token instead of token to allow for recentering of same token (set to new object with same token string)
  const [flowEditorNodeTokenToCenter, setFlowEditorNodeTokenToCenter] = useState<{
    token: string;
  } | null>(null);

  const flowEditorPathMatch = matchPath(
    {
      path: FLOW_EDITOR_BASE_PATH,
      end: false,
    },
    pathname,
  );

  const screenEditorPathMatch = matchPath(
    {
      path: SCREEN_EDITOR_BASE_PATH,
      end: false,
    },
    pathname,
  );

  const workflowEditorPathMatch = matchPath(
    {
      path: WORKFLOW_EDITOR_BASE_PATH,
      end: false,
    },
    pathname,
  );

  const workflowEditorStepTokenPathMatch = matchPath(
    {
      path: WORKFLOW_EDITOR_STEP_TOKEN_PATH,
      end: false,
    },
    pathname,
  );

  const reviewChangesPathMatch = matchPath(
    {
      path: REVIEW_CHANGES_PATH,
      end: false,
    },
    pathname,
  );

  const setSelectedComponentAndStep = (selectedComponentAndStep: SelectedComponentAndStep) => {
    // keep the url synced to the current selected component/step
    if (screenEditorPathMatch) {
      if (selectedComponentAndStep?.step.name && selectedComponentAndStep.component.name) {
        navigate(
          generatePath(SCREEN_EDITOR_PATH, {
            inquiryTemplateId: inquiryTemplate.id,
            inquiryTemplateVersionId: pathInquiryTemplateVersionId,
            stepName: selectedComponentAndStep.step.name,
            combinedStepScreenNameOrComponentName: selectedComponentAndStep.component.name,
          }),
          { replace: true },
        );
      } else if (selectedComponentAndStep?.step.name) {
        navigate(
          generatePath(SCREEN_EDITOR_STEP_PATH, {
            inquiryTemplateId: inquiryTemplate.id,
            inquiryTemplateVersionId: pathInquiryTemplateVersionId,
            stepName: selectedComponentAndStep.step.name,
          }),
          { replace: true },
        );
      }
    }
    setRawSelectedComponentAndStep(selectedComponentAndStep);
  };

  // call this function to center flow editor to node from anywhere else in editor after the initial deep link handling
  const navigateToFlowEditorAndSelectToken = (nodeToken: string) => {
    if (!flowEditorPathMatch) {
      navigate(
        generatePath(FLOW_EDITOR_BASE_PATH, {
          inquiryTemplateId: inquiryTemplate.id,
          inquiryTemplateVersionId: pathInquiryTemplateVersionId,
        }),
      );
    }
    setFlowEditorNodeTokenToCenter({ token: nodeToken });
  };

  // clear selected step and component selection for proper selection
  // of screen when selecting one in flow editor
  useEffect(() => {
    if (flowEditorPathMatch && selectedComponentAndStep != null) {
      setSelectedComponentAndStep(null);
    }
  }, [flowEditorPathMatch, selectedComponentAndStep]);

  // redux form selectors
  const enabledLocales = useSelector<RootState, InquiryIso6391LanguageCode[]>(
    (state) => selector(state, 'enabled-locales') ?? [],
  );

  const fieldSchema = useSelector<RootState, FieldSchema[]>(
    (state) => selector(state, 'field-schema') ?? [],
  );

  const steps = useSelector<RootState, InquiryTemplateVersionStep[]>(
    (state) => selector(state, 'steps') ?? [],
  );

  const themeAttributes = useSelector<RootState, ThemeAttributes>(
    (state) => selector(state, 'theme') ?? {},
  );

  // stepsPreview is stored only in redux and not in the form because
  // it's used only for preview purposes and isn't submitted in updates
  const stepsPreview = useSelector<RootState, StepPreview[]>(
    (state) => state.inquiryTemplate.stepsPreview ?? [],
  );

  const vtmplVersionMap = useSelector<RootState, VerificationTemplateVersionMap>(
    (state) => selector(state, 'verificationTemplateVersions') ?? {},
  );

  const workflowVersionMap = useSelector<RootState, WorkflowVersionMap>(
    (state) => selector(state, 'workflowVersions') ?? {},
  );

  const actionMeta = useSelector<RootState, WorkflowActionMeta>((state) =>
    getWorkflowActionMeta(state),
  );
  const workflowValidationContexts = useSelector(getAllWorkflowValidationContexts);
  const featureFlags = useSelector((state: RootState) => state.featureFlags.featureFlags);
  const { data: externalIntegrationVendors = [] } = useListExternalIntegrationVendorsQuery();
  const displayWorkflowErrors = !!submissionWorkflowErrors;
  const workflowValidationErrors = useMemo(() => {
    return displayWorkflowErrors
      ? mapValues((wfv) => {
          return validateWorkflowConfiguration({
            validationContext: workflowValidationContexts[wfv.id],
            outputs: wfv.attributes.outputs,
            timeOutMinutes: wfv.attributes['time-out-minutes'],
            externalIntegrationVendors,
          });
        }, workflowVersionMap)
      : {};
  }, [
    inputStep,
    featureFlags,
    displayWorkflowErrors,
    workflowVersionMap,
    workflowValidationContexts,
    actionMeta,
    workflowSchema,
  ]);

  // updated client data preview displayed while making edits in ITE
  const stepsPreviewMapLiveEdited = useMemo<StepPreviewMap>(
    () =>
      keyBy(
        'name',
        mapStepsConfigToStepsPreview({
          steps,
          stepsPreview,
          currentLocale: locale,
        }),
      ),
    [locale, steps, stepsPreview],
  );

  const previewPageMap = useMemo<PreviewPageMap>(
    () => generatePages(Object.values(stepsPreviewMapLiveEdited), false),
    [stepsPreviewMapLiveEdited],
  );

  const extractedWorkflowSteps = useMemo<LinkedWorkflowStep[]>(
    () => extractActionsAndConditionalNodes(workflowVersionMap),
    [workflowVersionMap],
  );

  const displayValidationErrorMessage = (message: string) =>
    dispatch(showMessage(message, MessageType.Failure));

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else if (submissionWorkflowErrors) {
      // Handle this in a useEffect here instead of inside the submission block because we need access to stepsTree
      //
      // if workflow errors on submit changed, highlight the first errored node from it
      // compute using the submission errors passed from parent component, because we don't want to recenter on updates inside template editor,
      // And, if for some reason the the workflow errors computed within this component differ from that of the parent, we would get some indication
      // as to which node it is, because it would get automatically selected and centered, but not marked as errored
      //
      // The errors computed from the submission block also only takes into account referenced wfvs, so we won't accidentally try to
      // focus a node in an unreferenced wfv
      if (workflowEditorStepTokenPathMatch) {
        const renderedNode = extractedWorkflowSteps.find(
          (workflowNode) =>
            workflowNode.workflowVersionId ===
              workflowEditorStepTokenPathMatch.params.workflowVersionId &&
            workflowNode.workflowStep.token ===
              workflowEditorStepTokenPathMatch.params.workflowStepToken,
        );

        if (renderedNode) {
          const wfvId = workflowEditorStepTokenPathMatch.params.workflowVersionId;

          if (wfvId) {
            const errorsForNode = validationErrorsForWorkflowNode(
              renderedNode,
              workflowVersionMap[wfvId]?.attributes['steps-config'] ?? [],
              submissionWorkflowErrors[wfvId] ?? [],
            );

            // if current workflow editor slice has errors, stay on it, otherwise navigate to flow editor and center an errored workflow node
            if (errorsForNode.length > 0) {
              displayValidationErrorMessage(WORKFLOW_VALIDATION_ERROR_MESSAGE);
              return;
            }
          }
        }
      }

      const nodeToHighlight = getFirstWorkflowValidationErrorNode(
        extractedWorkflowSteps,
        workflowVersionMap,
        submissionWorkflowErrors,
      );

      // nodeToHighlight can be null if you insert an errored workflow step then delete the step referencing it, which removes it from the tree
      // we still trigger errors on submission but it is no longer accessible through the steps tree
      if (nodeToHighlight) {
        const workflowVersionId = nodeToHighlight.token.split('.')[0];
        const erroredField = submissionWorkflowErrors[workflowVersionId][0].field;
        let highlightedNodeToken = nodeToHighlight.token;
        // if error is found in a specific branch of a conditional node,
        // pan directly to the branch conditional node rather than the root for accuracy
        if (erroredField?.match(new RegExp(`^(conditionals|branches)`))) {
          // the second erroredField index usually contains the branchIndex, but can be
          // null when the error is that all non-else branches have been deleted.
          const branchIndex: string | undefined = erroredField.match(/\[(.*?)\]/)?.[1];
          highlightedNodeToken =
            branchIndex != null
              ? `${nodeToHighlight.token}.${parseInt(branchIndex)}`
              : highlightedNodeToken;
        }
        navigateToFlowEditorAndSelectToken(highlightedNodeToken);
      }

      // this callback causes the 'one or more workflows has errored message' to show up. We call this here
      // instead of one component higher in the tree so that it happens after the navigation to the flow editor,
      // which would otherwise hide this message.
      displayValidationErrorMessage(WORKFLOW_VALIDATION_ERROR_MESSAGE);
    } else if (submissionComponentErrors != null) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const firstErroredComponent: [string, ComponentValidationError[]] =
        Object.entries(submissionComponentErrors).at(0);

      const [componentPath, [firstError, ..._rest]] = firstErroredComponent;

      const selectedStep = steps.find(
        (step) => step.name === firstError.stepName,
      ) as InquiryTemplateVersionStepUi;

      const selectedComponent = get(componentPath, selectedStep.config.components);

      setSelectedComponentAndStep({
        component: selectedComponent,
        step: selectedStep,
        error: true,
      });

      if (!screenEditorPathMatch) {
        navigate(
          generatePath(SCREEN_EDITOR_STEP_PATH, {
            inquiryTemplateId: inquiryTemplate.id,
            inquiryTemplateVersionId: pathInquiryTemplateVersionId,
            stepName: selectedStep.name,
          }),
        );
      }
      displayValidationErrorMessage(firstError.message);
    }
  }, [submissionWorkflowErrors, submissionComponentErrors]);

  useEffect(() => {
    if (refreshWorkflowSchema && (flowEditorPathMatch || workflowEditorPathMatch)) {
      setRefreshWorkflowSchema(false);
      const updatedWorkflowSchema = updateWorkflowSchemaInquiryFields(
        workflowSchema,
        fieldSchema,
        inquiryTemplate.id,
      );
      dispatch(
        generateAllWorkflowStepContexts({
          inquiryTemplateId: inquiryTemplate.id,
          workflowSchemaOverride: updatedWorkflowSchema,
        }),
      );
    }
  }, [
    workflowEditorPathMatch,
    flowEditorPathMatch,
    workflowSchema,
    fieldSchema,
    inquiryTemplate.id,
  ]);

  const handleUpdateWorkflowVersions = useCallback(
    (workflowVersionMap: WorkflowVersionMap) => change('workflowVersions', workflowVersionMap),
    [change],
  );
  const handleUpdateFormValues = useCallback(
    ({
      steps,
      stepsPreview,
      workflowVersionMap: updatedWorkflowVersionMap,
      updatedWorkflowVersion,
      vtmplVersionUpdates,
      fieldSchema,
      themeAttributes,
    }: FlowTreeFormValues) => {
      batch(() => {
        if (steps) {
          change('steps', steps);
        }
        if (stepsPreview) {
          // dispatch update to client data in redux separately
          // as it's used for preview purposes only and is not
          // part of the form data
          dispatch(setStepsPreview({ stepsPreview }));
        }
        if (updatedWorkflowVersionMap) {
          handleUpdateWorkflowVersions(updatedWorkflowVersionMap);
        }
        // TEMP - TODO (eric): remove when flow tree update logic
        // is refactored to consistently return full map
        if (updatedWorkflowVersion) {
          handleUpdateWorkflowVersions({
            ...workflowVersionMap,
            [updatedWorkflowVersion.id]: updatedWorkflowVersion,
          });
        }
        if (vtmplVersionUpdates) {
          const { versionMap, verificationTemplates } = vtmplVersionUpdates;
          change('verificationTemplateVersions', versionMap);
          if (verificationTemplates != null) {
            handleUpdateStoredVerificationTemplates(verificationTemplates);
          }
        }
        if (fieldSchema) {
          change('field-schema', fieldSchema);
        }
        if (themeAttributes) {
          change('theme', themeAttributes);
        }
      });
    },
    [
      workflowVersionMap,
      workflowSchema,
      inquiryTemplate.id,
      change,
      dispatch,
      handleUpdateWorkflowVersions,
      handleUpdateStoredVerificationTemplates,
    ],
  );

  const shortcuts = useMemo(() => {
    const baseShortcuts = cloneDeep(ITE_BASE_SHORTCUTS);

    if (screenEditorPathMatch) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      baseShortcuts
        .find((section) => section.label === 'editing')
        .shortcuts.push(...Object.values(shortcutsMap));
    }

    return flowEditorPathMatch || workflowEditorPathMatch
      ? baseShortcuts
      : [...baseShortcuts.filter((section) => section.label === 'editing')];
  }, [flowEditorPathMatch, workflowEditorPathMatch, screenEditorPathMatch]);

  // Don't show the BottomPanel in the diff viewer -- users should have resolved errors before publishing
  const showBottomPanel = allowSavingInvalidItvDraft && !enableLeftNav && !reviewChangesPathMatch;

  if (firstInitialStep(steps) == null) {
    captureMessage(
      `No initial step found for template version ${inquiryTemplateVersion.id} to construct InquiryTemplateTree`,
      'warning',
    );
    return (
      <Backdrop>
        <NullState
          message='Sorry, we’re unable to display the flow editor.'
          backRoute='/dashboard/inquiry-templates'
        />
      </Backdrop>
    );
  }

  const currentVersionWithFormValues = pipe(
    set(['attributes', 'enabled-locales'], enabledLocales),
    set(['attributes', 'field-schema'], fieldSchema),
    set(['attributes', 'steps'], steps),
    set(['attributes', 'theme'], themeAttributes),
  )(inquiryTemplateVersion) as InquiryTemplateVersion;

  return (
    <form
      onKeyDown={(e) => {
        if (e.target instanceof HTMLElement && e.target.tagName === 'TEXTAREA') {
          return;
        }
        return e.key === 'Enter' && e.preventDefault();
      }}
    >
      <ModalWrapper
        shouldCloseOnEsc
        isOpen={shouldShowShortcutsModal}
        handleCloseModal={hideShortcutsModal}
      >
        <ShortcutsModal handleClose={hideShortcutsModal} shortcuts={shortcuts} />
      </ModalWrapper>

      <InquiryTemplateEditorContainer
        withBanner={shouldDisplayBanner(inquiryTemplate, inquiryTemplateVersion)}
        showingBottomPanel={showBottomPanel}
      >
        <Suspense fallback={<LoadingModal title='Loading...' isLoading />}>
          <SentryRoutes>
            {[
              SCREEN_EDITOR_STEP_RELATIVE_PATH,
              SCREEN_EDITOR_COMBINED_RELATIVE_PATH,
              SCREEN_EDITOR_RELATIVE_PATH,
            ].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <InquiryTemplateScreenEditor
                    inquiryTemplate={inquiryTemplate}
                    inquiryTemplateVersion={currentVersionWithFormValues}
                    baseRoutePath={basePath}
                    stepsPreviewMap={stepsPreviewMapLiveEdited}
                    pages={Object.values(previewPageMap).flatMap((pages) => pages)}
                    currentLocale={locale}
                    handleLoadStepLocalizedStrings={handleLoadStepLocalizedStrings}
                    handleSetLocale={handleSetLocale}
                    workflowVersionMap={workflowVersionMap}
                    selectedComponentAndStep={selectedComponentAndStep}
                    setSelectedComponentAndStep={setSelectedComponentAndStep}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    showShortcutsModal={keydownShortcutsEnabled ? showShortcutsModal : null}
                    handleUpdateFormValues={handleUpdateFormValues}
                    submissionComponentErrors={submissionComponentErrors}
                  />
                }
              />
            ))}

            {[
              FLOW_EDITOR_ROUTE_INDEX_RELATIVE_PATH,
              FLOW_EDITOR_NODE_TOKEN_RELATIVE_PATH,
              FLOW_EDITOR_RELATIVE_PATH,
            ].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <WorkflowActionMetaProvider
                    // Needed to make sure providers are separate between flow and workflow editor contexts
                    key='fle-flow-editor-provider'
                    context='fle-flow-editor'
                    externalIntegrationVendors={externalIntegrationVendors}
                  >
                    <InquiryTemplateFlowEditor
                      inquiryTemplateVersion={currentVersionWithFormValues}
                      partialInquiryTemplateVersions={partialInquiryTemplateVersions}
                      basePath={`${basePath}/flow`}
                      stepsPreviewMap={stepsPreviewMapLiveEdited}
                      previewPageMap={previewPageMap}
                      currentLocale={locale}
                      extractedWorkflowSteps={extractedWorkflowSteps}
                      moduleOptions={moduleOptions}
                      modulePreviewMap={modulePreviewMap}
                      nodeTokenObjectToCenter={flowEditorNodeTokenToCenter}
                      setNodeTokenObjectToCenter={setFlowEditorNodeTokenToCenter}
                      workflowVersionMap={workflowVersionMap}
                      workflowValidationErrors={workflowValidationErrors}
                      vtmplVersionMap={vtmplVersionMap}
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      initialFlowViewTransformationMatrix={
                        savedFlowViewTransformationMatrixRef.current
                      }
                      isShowingInquiryTemplateVersions={isShowingInquiryTemplateVersions}
                      isFormPristine={isFormPristine}
                      handleAddLocalDraftVersion={handleAddLocalDraftVersion}
                      handleDiscardDraft={handleDiscardDraft}
                      handleLoadModule={handleLoadModule}
                      handleLoadModulePreview={handleLoadModulePreview}
                      handleSelectVersion={handleSelectVersion}
                      handleSetLocale={handleSetLocale}
                      handleUpdateFormValues={handleUpdateFormValues}
                      hideInquiryTemplateVersions={hideInquiryTemplateVersions}
                      onFlowViewTransformationMatrixChange={
                        debouncedSetSavedFlowViewTransformationMatrix
                      }
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      showShortcutsModal={keydownShortcutsEnabled ? showShortcutsModal : null}
                      bottomPanel={
                        allowSavingInvalidItvDraft && (
                          <BottomPanel validationErrors={validationErrors} />
                        )
                      }
                    />
                  </WorkflowActionMetaProvider>
                }
              />
            ))}
            <Route
              path={CONFIG_EDITOR_RELATIVE_PATH + '/*'}
              element={
                <InquiryTemplateConfigEditor
                  basePath={basePath}
                  currentLocale={locale}
                  isFormPristine={isFormPristine}
                  handleSetLocale={handleSetLocale}
                  inquiryTemplateVersion={currentVersionWithFormValues}
                  stepsPreviewMap={stepsPreviewMapLiveEdited}
                  previewPageMap={previewPageMap}
                  extractedWorkflowSteps={extractedWorkflowSteps}
                  vtmplVersionMap={vtmplVersionMap}
                  workflowVersionMap={workflowVersionMap}
                  matchRequirementProfileMap={matchRequirementProfileMap}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  showShortcutsModal={keydownShortcutsEnabled ? showShortcutsModal : null}
                  handleUpdateFormValues={handleUpdateFormValues}
                  handleLoadModule={handleLoadModule}
                />
              }
            />
            {[
              WORKFLOW_EDITOR_CONDITIONAL_ROUTE_RELATIVE_PATH,
              WORKFLOW_EDITOR_STEP_TOKEN_RELATIVE_PATH,
              WORKFLOW_EDITOR_RELATIVE_PATH,
            ].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <WorkflowActionMetaProvider
                    // Needed to make sure providers are separate between flow and workflow editor contexts
                    key='fle-workflow-editor-provider'
                    context='fle-workflow-editor'
                    externalIntegrationVendors={externalIntegrationVendors}
                  >
                    <InquiryTemplateWorkflowEditor
                      isLoading={isLoading}
                      workflowVersionMap={workflowVersionMap}
                      workflowValidationErrors={workflowValidationErrors}
                      handleUpdateWorkflowVersions={handleUpdateWorkflowVersions}
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      showShortcutsModal={keydownShortcutsEnabled ? showShortcutsModal : null}
                    />
                  </WorkflowActionMetaProvider>
                }
              />
            ))}
            <Route
              path={REVIEW_CHANGES_RELATIVE_PATH}
              element={
                <WorkflowActionMetaProvider
                  // Needed to make sure providers are separate between flow and workflow editor contexts
                  key='fle-workflow-editor-provider'
                  context='fle-workflow-editor'
                  externalIntegrationVendors={externalIntegrationVendors}
                >
                  <InquiryTemplateVersionDiff
                    // Inquiry Template Json Version Diff Props
                    inquiryTemplate={inquiryTemplate}
                    currentSchema={currentSchema}
                    comparedVersion={comparedVersion}
                    comparedSchema={comparedSchema}
                    loadingComparedVersion={loadingComparedVersion}
                    handleLoadComparedVersion={handleLoadComparedVersion}
                    // Inquiry Template Visual Version Diff Props
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    versionDiff={versionDiff}
                    loadingVersionDiff={loadingVersionDiff}
                    inquiryTemplateVersion={inquiryTemplateVersion}
                    workflowVersionMap={workflowVersionMap}
                    workflowSchema={workflowSchema}
                    verificationTemplateVersionMap={vtmplVersionMap}
                    handleLoadVersionDiff={handleLoadVersionDiff}
                    stepsPreviewMap={stepsPreviewMapLiveEdited}
                    previewPageMap={previewPageMap}
                    currentLocale={locale}
                  />
                </WorkflowActionMetaProvider>
              }
            />

            <Route path='*' element={<Navigate to='/dashboard/inquiry-templates' replace />} />
          </SentryRoutes>
          {showBottomPanel && (
            <BottomPanelContainer>
              <BottomPanel validationErrors={validationErrors} />
            </BottomPanelContainer>
          )}
        </Suspense>
      </InquiryTemplateEditorContainer>
    </form>
  );
};

export default reduxForm<InquiryTemplateEditorFormData, InquiryTemplateEditorProps>({
  form: INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
  enableReinitialize: true,
  onChange: (values: InquiryTemplateEditorFormData, dispatch, props, previousValues) => {
    if (props.allowSavingInvalidItvDraft) {
      props.updateValidationErrors(values);
    }
    dispatch(updateHistory(values));

    if (!isEmpty(previousValues)) {
      if (values['field-schema'] !== previousValues['field-schema']) {
        props.setRefreshWorkflowSchema(true);
      }
      if (values.workflowVersions !== previousValues.workflowVersions) {
        const changedWfvs = Object.keys(values.workflowVersions).reduce<WorkflowVersionMap>(
          (filtered, wfvId) => {
            const currentValue = values.workflowVersions[wfvId];
            if (currentValue !== previousValues.workflowVersions?.[wfvId]) {
              filtered[wfvId] = currentValue;
            }
            return filtered;
          },
          {},
        );
        dispatch(
          generateAllWorkflowStepContexts({
            inquiryTemplateId: props.inquiryTemplate.id,
            versionsMapOverride: changedWfvs,
          }),
        );
      }
    }
  },
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(InquiryTemplateEditor);
